import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  EMPLOYEE_EQUIPMENT_QUERY_KEY,
  EMPLOYEE_NOTICE_QUERY_KEY,
} from '@/components/email-templates/constants';
import EmployeeNotificationModal from '@/components/email-templates/EmployeeNotificationModal';
import EquipmentRequestModal from '@/components/equipment-requests/EquipmentRequestModal';
import { getEmployeeNotificationState } from '@/services/emailTemplates.service';
import {
  getEmployeeEquipment,
  getOwnEquipment,
} from '@/services/equipmentRequests.service';

/**
 * The new-employee actions of the profile: the notice to colleagues (from
 * the company's email template) and the equipment request. The server
 * decides who may use them; a refusal leaves both out.
 *
 * On the employee's own profile (`isOwn`) only the equipment: their own
 * requests (waiting for the head of the sector, approved or rejected) and
 * "Zatraži opremu". On someone else's, `equipmentEnabled` also lets a head
 * of the sector who holds only the employee role see the tile, to approve.
 */
export default function useEmployeeOnboarding({
  employeeId,
  employeeName,
  enabled,
  equipmentEnabled = enabled,
  isOwn = false,
}) {
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);

  const { data: notice } = useQuery({
    queryKey: [EMPLOYEE_NOTICE_QUERY_KEY, employeeId],
    queryFn: () => getEmployeeNotificationState(employeeId),
    enabled: !!employeeId && enabled,
    retry: false,
  });
  const { data: equipment } = useQuery({
    // The manager view keeps the key the termination dialog shares.
    queryKey: isOwn
      ? [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId, 'own']
      : [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId],
    queryFn: () =>
      isOwn ? getOwnEquipment(employeeId) : getEmployeeEquipment(employeeId),
    enabled: !!employeeId && (isOwn || equipmentEnabled),
    retry: false,
  });

  const closeEquipment = () => setEquipmentOpen(false);

  const canNotify = !!notice?.templates?.length;
  const canRequestEquipment = !!equipment?.enabled;
  const lastNotice = notice?.sends?.[0] || null;

  const modals = (
    <>
      {canNotify && (
        <EmployeeNotificationModal
          open={noticeOpen}
          onClose={() => setNoticeOpen(false)}
          employeeId={employeeId}
          state={notice}
        />
      )}
      {canRequestEquipment && (
        <EquipmentRequestModal
          open={equipmentOpen}
          onClose={closeEquipment}
          employeeId={employeeId}
          employeeName={employeeName}
          groups={equipment.groups || []}
          otherItemsService={equipment.otherItemsService}
          header={{
            newHire: !!equipment.newHire,
            position: equipment.position || '',
            sectorName: equipment.sectorName || '',
          }}
          self={isOwn}
        />
      )}
    </>
  );

  return {
    notice: notice || null,
    equipment: equipment || null,
    canNotify,
    canRequestEquipment,
    lastNotice,
    isOwnEquipment: isOwn,
    // The tile exists once there is something to do or to see on it.
    showEquipmentTile:
      canRequestEquipment ||
      !!equipment?.requests?.length ||
      !!equipment?.awaiting?.length,
    openNotice: () => setNoticeOpen(true),
    openEquipment: () => setEquipmentOpen(true),
    modals,
  };
}
